import React, { useEffect, useState, useMemo } from "react";
import CategoriesPieChart from "./CategoriesPieChart";
import * as XLSX from "xlsx";
import Navbar from "./Navbar";
import authStore from "../services/authStore";

const API_BASE_URL = process.env.REACT_APP_API_URL;

function flattenRow(row) {
  const out = {};

  if (!row || typeof row !== "object") {
    return { value: String(row) };
  }

  Object.keys(row).forEach((k) => {
    const v = row[k];

    if (v === null || v === undefined) {
      out[k] = "";
    } else if (typeof v === "object") {
      out[k] = Array.isArray(v) ? v.join(", ") : JSON.stringify(v);
    } else {
      out[k] = v;
    }
  });

  return out;
}

function timestampForFile() {
  const d = new Date();

  const pad = (n) => String(n).padStart(2, "0");

  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(
    d.getDate(),
  )}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

export default function Landing() {
  const [summary, setSummary] = useState([]);
  const [purSummary, setPurSummary] = useState([]);

  const [leads, setLeads] = useState([]);
  const [events, setEvents] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPurpose, setSelectedPurpose] = useState(null);

  const [selectedEventId, setSelectedEventId] = useState("");

  const [showPopup, setShowPopup] = useState(false);
  const [popupImages, setPopupImages] = useState([]);
  const [popupLoading, setPopupLoading] = useState(false);

  // =========================
  // FETCH EVENTS
  // =========================
  async function fetchEvents() {
    try {
      const token = authStore.getToken();

      const res = await fetch(`${API_BASE_URL}/event/GetEventList`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error(`Event API HTTP ${res.status}`);
      }

      const data = await res.json();

      let eventRows = [];

      if (Array.isArray(data)) {
        eventRows = data;
      } else if (Array.isArray(data.data)) {
        eventRows = data.data;
      }

      setEvents(eventRows);
    } catch (err) {
      console.error("EVENT ERROR:", err);
    }
  }

  // =========================
  // FETCH LEADS
  // =========================
  async function fetchAll() {
    setLoading(true);
    setError(null);

    try {
      const token = authStore.getToken();

      let url = `${API_BASE_URL}/enquiry/GetEnquiryList`;

      // dynamic eventid query param
      if (selectedEventId) {
        url += `?eventid=${selectedEventId}`;
      }

      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error(`Enquiry API HTTP ${res.status}`);
      }

      const leadsPayload = await res.json();

      let leadRows = [];

      if (Array.isArray(leadsPayload)) {
        leadRows = leadsPayload;
      } else if (Array.isArray(leadsPayload.data)) {
        leadRows = leadsPayload.data;
      } else if (Array.isArray(leadsPayload.rows)) {
        leadRows = leadsPayload.rows;
      } else {
        const arrProp = Object.values(leadsPayload || {}).find((v) =>
          Array.isArray(v),
        );

        if (arrProp) {
          leadRows = arrProp;
        }
      }

      setLeads(Array.isArray(leadRows) ? leadRows : []);
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }

  // =========================
  // INITIAL LOAD
  // =========================
  useEffect(() => {
    fetchEvents();
    fetchAll();
  }, []);

  // =========================
  // EVENT CHANGE
  // =========================
  useEffect(() => {
    fetchAll();
  }, [selectedEventId]);

  // =========================
  // TABLE COLUMNS
  // =========================
  const leadColumns = useMemo(() => {
    if (!leads || leads.length === 0) return [];

    const set = new Set();

    leads.forEach((r) => {
      if (r && typeof r === "object") {
        Object.keys(r).forEach((k) => set.add(k));
      }
    });

    return Array.from(set);
  }, [leads]);

  // =========================
  // FILTERED LEADS
  // =========================
  const filteredLeads = useMemo(() => {
    if ((!selectedCategory && !selectedPurpose) || !leads) {
      return leads;
    }

    return leads.filter((row) => {
      let ok = true;

      if (selectedCategory) {
        const cats = (row.CATEGORIES || row.categories || "")
          .toString()
          .toLowerCase();

        ok = ok && cats.includes(selectedCategory.toLowerCase());
      }

      if (selectedPurpose) {
        const pur = (row.PURPOSE || row.purpose || "").toString().toLowerCase();

        ok = ok && pur.includes(selectedPurpose.toLowerCase());
      }

      return ok;
    });
  }, [leads, selectedCategory, selectedPurpose]);

  // =========================
  // EXPORT EXCEL
  // =========================
  function exportToExcel() {
    if (!filteredLeads?.length) return;

    const prepared = filteredLeads.map(flattenRow);

    const ws = XLSX.utils.json_to_sheet(prepared, {
      skipHeader: false,
    });

    const cols = leadColumns.length
      ? leadColumns
      : Object.keys(prepared[0] || {});

    ws["!cols"] = cols.map((c) => {
      let max = c.length;

      prepared.forEach((r) => {
        const val = r[c] === undefined || r[c] === null ? "" : String(r[c]);

        if (val.length > max) max = val.length;
      });

      return {
        wch: Math.min(Math.max(max + 2, 10), 50),
      };
    });

    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Leads");

    XLSX.writeFile(wb, `leads_${timestampForFile()}.xlsx`);
  }

  // =========================
  // FETCH ATTACHMENTS
  // =========================
  async function getEnquiryImages(enquiryId) {
    const token = authStore.getToken();

    const res = await fetch(
      `${API_BASE_URL}/enquiry/GetEnquiryAttachments/${enquiryId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!res.ok) {
      throw new Error("Failed to load attachments");
    }

    return await res.json();
  }

  // =========================
  // OPEN POPUP
  // =========================
  const openAttachmentPopup = async (enquiryId) => {
    try {
      setPopupLoading(true);

      const images = await getEnquiryImages(enquiryId);

      console.log("ATTACHMENTS:", images);

      setPopupImages(images.data || []);

      setShowPopup(true);
    } catch (err) {
      console.error(err);
      alert("No images found");
    } finally {
      setPopupLoading(false);
    }
  };

  // =========================
  // DOWNLOAD FILE
  // =========================
  function downloadBase64File(base64Data, fileName) {
    try {
      if (!base64Data) {
        alert("Image data missing");
        return;
      }

      const safeFileName =
        fileName && fileName.trim() !== ""
          ? fileName
          : `image_${Date.now()}.jpg`;

      let mimeType = "image/jpeg";

      if (safeFileName.toLowerCase().endsWith(".png")) {
        mimeType = "image/png";
      }

      const byteCharacters = atob(base64Data);

      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], {
        type: mimeType,
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;
      link.download = safeFileName;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (err) {
      console.error("DOWNLOAD ERROR:", err);
    }
  }

  if (loading) {
    return <div className="p-3">Loading...</div>;
  }

  if (error) {
    return <div className="p-3 text-danger">Error: {error}</div>;
  }

  return (
    <>
      <Navbar />

      <div className="page-content">
        <div className="container mt-4">
          <div className="row g-3">
            {/* CATEGORY */}
            <div className="col-12 col-md-6">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-2">
                    <h6 className="card-title mb-0 me-auto">
                      Category Summary
                    </h6>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setSelectedCategory(null)}
                    >
                      Clear
                    </button>
                  </div>

                  <CategoriesPieChart
                    data={summary}
                    onSelect={(name) => setSelectedCategory(name)}
                    selected={selectedCategory}
                  />
                </div>
              </div>
            </div>

            {/* PURPOSE */}
            <div className="col-12 col-md-6">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-2">
                    <h6 className="card-title mb-0 me-auto">Purpose Summary</h6>

                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setSelectedPurpose(null)}
                    >
                      Clear
                    </button>
                  </div>

                  <CategoriesPieChart
                    data={purSummary}
                    onSelect={(name) => setSelectedPurpose(name)}
                    selected={selectedPurpose}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* LEADS TABLE */}
        <div className="container mt-4">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              {/* HEADER */}
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <h6 className="mb-0 fw-semibold">Leads</h6>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <select
                    value={selectedEventId}
                    onChange={(e) => setSelectedEventId(e.target.value)}
                    style={{
                      width: "220px",
                      height: "38px",
                      border: "1px solid #ced4da",
                      borderRadius: "10px",
                      padding: "0 35px 0 12px",
                      fontSize: "14px",
                      backgroundColor: "#fff",
                      color: "#212529",
                      outline: "none",
                      cursor: "pointer",
                      appearance: "auto",
                      WebkitAppearance: "menulist",
                      MozAppearance: "menulist",
                    }}
                  >
                    <option value="">All Events</option>

                    {events.map((event) => (
                      <option key={event.id} value={event.id}>
                        {event.eventname}
                      </option>
                    ))}
                  </select>

                  {/* CLEAR */}
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => {
                      setSelectedCategory(null);
                      setSelectedPurpose(null);
                    }}
                    style={{
                      height: "36px",
                      borderRadius: "8px",
                      padding: "0 14px",
                    }}
                  >
                    Clear All
                  </button>

                  {/* EXPORT */}
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={exportToExcel}
                    disabled={!filteredLeads?.length}
                    style={{
                      height: "36px",
                      borderRadius: "8px",
                      padding: "0 14px",
                    }}
                  >
                    Download Excel
                  </button>
                </div>
              </div>

              {/* TABLE */}
              <div className="table-responsive">
                <table className="table table-striped table-bordered align-middle">
                  <thead className="table-light">
                    <tr>
                      {leadColumns.map((c) => (
                        <th key={c}>{c}</th>
                      ))}

                      <th className="text-center">Attachment</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredLeads?.length > 0 ? (
                      filteredLeads.map((row, i) => (
                        <tr key={i}>
                          {leadColumns.map((col) => {
                            const cell = row[col];

                            const display =
                              cell === null || cell === undefined
                                ? ""
                                : typeof cell === "object"
                                  ? Array.isArray(cell)
                                    ? cell.join(", ")
                                    : JSON.stringify(cell)
                                  : String(cell);

                            return <td key={col}>{display}</td>;
                          })}

                          {/* ATTACHMENT */}
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-outline-dark"
                              onClick={() =>
                                openAttachmentPopup(row.Id || row.ID || row.id)
                              }
                              style={{
                                width: "34px",
                                height: "34px",
                                borderRadius: "8px",
                              }}
                            >
                              👁️
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={leadColumns.length + 1}
                          className="text-center py-4 text-muted"
                        >
                          No leads found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* IMAGE POPUP */}
        {showPopup && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.7)",
              zIndex: 10000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                background: "#fff",
                padding: 20,
                borderRadius: 12,
                width: "90%",
                maxWidth: "1200px",
                maxHeight: "85vh",
                overflowY: "auto",
              }}
            >
              {/* HEADER */}
              <div className="d-flex justify-content-between mb-3">
                <h5>Attachments</h5>
                <button
                  onClick={() => setShowPopup(false)}
                  className="btn btn-sm btn-danger"
                >
                  ✖
                </button>
              </div>

              {popupLoading ? (
                <div className="text-center p-4">Loading images...</div>
              ) : popupImages.length === 0 ? (
                <div className="text-center text-muted">No images found</div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      window.innerWidth <= 768 ? "1fr" : "repeat(5, 1fr)",
                    gap: 12,
                  }}
                >
                  {popupImages.map((img, index) => (
                    <div
                      key={index}
                      style={{
                        border: "1px solid #eee",
                        borderRadius: 10,
                        padding: 10,
                        textAlign: "center",
                      }}
                    >
                      <img
                        src={`data:image/png;base64,${img.Base64Data}`}
                        alt={img.FileNAME}
                        style={{
                          width: "100%",
                          height: 220,
                          objectFit: "cover",
                          borderRadius: 8,
                        }}
                      />

                      <div
                        className="mt-2 text-truncate"
                        style={{
                          fontSize: 12,
                        }}
                      >
                        {img.FileNAME}
                      </div>

                      <button
                        className="btn btn-success btn-sm mt-2"
                        onClick={() =>
                          downloadBase64File(img.Base64Data, img.FileNAME)
                        }
                      >
                        ⬇ Download
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
